/**
 * Toast — shared snackbar-style notifications for demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GRAY, PRIMARY, SAFE, WARN } from "../design-system/index.js";
import { useReducedMotion } from "../hooks/index.js";

export type ToastTone = "info" | "success" | "warning" | "error";

export interface ToastMessage {
  readonly id: number;
  readonly message: string;
  readonly tone: ToastTone;
}

export interface ToastApi {
  readonly toast: (message: string, tone?: ToastTone) => void;
  readonly toasts: readonly ToastMessage[];
  readonly dismiss: (id: number) => void;
}

export interface ToastViewportProps {
  readonly toasts: readonly ToastMessage[];
  readonly onDismiss?: (id: number) => void;
  readonly className?: string;
}

const TONE_STYLE: Readonly<
  Record<ToastTone, { border: string; background: string; color: string }>
> = {
  info: {
    border: PRIMARY[300],
    background: PRIMARY[100],
    color: PRIMARY[900],
  },
  success: {
    border: SAFE[300],
    background: SAFE[100],
    color: SAFE[700],
  },
  warning: {
    border: "#fed7aa",
    background: "#fff7ed",
    color: "#9a3412",
  },
  error: {
    border: WARN[300],
    background: WARN[100],
    color: WARN[700],
  },
};

export function useToast(durationMs = 2400): ToastApi {
  const [toasts, setToasts] = useState<readonly ToastMessage[]>([]);
  const idRef = useRef(0);
  const timeoutsRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
      for (const handle of timeoutsRef.current.values()) {
        clearTimeout(handle);
      }
      timeoutsRef.current.clear();
    };
  }, []);

  const dismiss = useCallback((id: number): void => {
    const handle = timeoutsRef.current.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      timeoutsRef.current.delete(id);
    }
    if (!mountedRef.current) return;
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, tone: ToastTone = "info"): void => {
      if (!mountedRef.current) return;
      const id = (idRef.current += 1);
      setToasts((current) => [...current, { id, message, tone }]);
      if (durationMs <= 0) return;
      const handle = setTimeout((): void => {
        dismiss(id);
      }, durationMs);
      timeoutsRef.current.set(id, handle);
    },
    [dismiss, durationMs],
  );

  return { toast, toasts, dismiss };
}

export function ToastViewport({
  toasts,
  onDismiss,
  className,
}: ToastViewportProps): React.JSX.Element | null {
  const reducedMotion = useReducedMotion();
  if (typeof document === "undefined") return null;

  const viewport = (
    <div
      aria-atomic="false"
      aria-live="polite"
      className={className}
      role="status"
      style={{
        bottom: 16,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: "min(28rem, calc(100vw - 2rem))",
        pointerEvents: "none",
        position: "fixed",
        right: 16,
        zIndex: 80,
      }}
    >
      {toasts.map((toast) => {
        const tone = TONE_STYLE[toast.tone];
        return (
          <div
            key={toast.id}
            style={{
              alignItems: "center",
              background: tone.background,
              border: `1px solid ${tone.border}`,
              borderRadius: 8,
              boxShadow: "0 12px 28px rgba(15, 23, 42, 0.18)",
              color: tone.color,
              display: "flex",
              fontSize: 13,
              fontWeight: 500,
              gap: 12,
              justifyContent: "space-between",
              lineHeight: 1.4,
              minHeight: 40,
              padding: "9px 12px",
              pointerEvents: "auto",
              transform: "translateZ(0)",
              transition: reducedMotion
                ? "none"
                : "opacity 140ms ease-out, transform 140ms ease-out",
            }}
          >
            <span>{toast.message}</span>
            {onDismiss !== undefined && (
              <button
                type="button"
                aria-label="Dismiss notification"
                onClick={() => {
                  onDismiss(toast.id);
                }}
                style={{
                  background: "transparent",
                  border: 0,
                  color: GRAY[600],
                  cursor: "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                x
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  return createPortal(viewport, document.body);
}
